import { useState } from "react";
import { router } from "@inertiajs/react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { wholesale_forecast_by_account_import_path } from "@/lib/routes";
import { useMarket } from "@/lib/useMarket";
import ImportFormatHint from "./ImportFormatHint";

interface Props {
  open: boolean;
  onClose: () => void;
  channelId: string | null;
}

export default function ImportDialog({ open, onClose, channelId }: Props) {
  const market = useMarket();
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function handleOpenChange(next: boolean) {
    if (!next && !submitting) {
      setFile(null);
      onClose();
    }
  }

  function submit() {
    if (!file) return;
    setSubmitting(true);
    const data = new FormData();
    data.append("file", file);
    if (channelId) data.append("channel_id", channelId);
    router.post(wholesale_forecast_by_account_import_path(market.slug), data, {
      forceFormData: true,
      onSuccess: () => {
        setFile(null);
        onClose();
      },
      onFinish: () => setSubmitting(false),
    });
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Import forecast spreadsheet</DialogTitle>
          <DialogDescription>
            Upload the team's CSV export to bulk-update units per product × customer × month.
          </DialogDescription>
        </DialogHeader>

        <ImportFormatHint />

        <div className="flex flex-col gap-1.5">
          <label htmlFor="forecast-csv" className="text-sm font-medium">CSV file</label>
          <input
            id="forecast-csv"
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            disabled={submitting}
            className="text-[13px] file:mr-3 file:rounded-md file:border file:border-input file:bg-background file:px-3 file:py-1.5 file:text-[12px] file:font-medium file:text-foreground hover:file:bg-accent"
          />
          {file && (
            <span className="text-[11px] text-muted-foreground">
              Selected: {file.name} ({Math.round(file.size / 1024)} KB)
            </span>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => handleOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button size="sm" onClick={submit} disabled={!file || submitting}>
            {submitting ? "Importing…" : "Import"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
